import { Checkbox, Switch, TextField, Typography } from "@material-ui/core";
import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import Grid from "@material-ui/core/Grid";
import Toolbar from "@material-ui/core/Toolbar";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import { useFormik } from "formik";
import { Autocomplete } from "@material-ui/lab";
import * as Yup from "yup";
import { SportUtils } from "@prestosports/prestostats-core/Common";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import configurationStyles from "../styles/Configuration.styles";
import commonStyles from "../styles/Common.styles";
import { groupBy, sortBy } from "../../../util/array-util";
import { getByPath } from "../../../util/object-util";
import { IconMap } from "./IconMap";

// Pure schema builders kept at module scope so the component can memoize
// them — rebuilding the ui schema and the full Yup schema on every render made
// the whole form feel sluggish.
const buildUiSchema = (config, customFormData, parent) => {
	const uiSchema = {};

	Object.entries(config).forEach(([key, value]) => {
		const fullPropPath = parent ? `${parent}.${key}` : key;
		if (value instanceof Object && !Array.isArray(value)) {
			uiSchema[key] = {
				...buildUiSchema(value, customFormData, fullPropPath),
				title: SportUtils.fromCamelCase(key),
				type: "object"
			};
		} else if (key !== "_type") {
			const formData = getByPath(customFormData, fullPropPath);

			uiSchema[key] = {
				key: fullPropPath,
				title: SportUtils.fromCamelCase(key),
				type:
					Array.isArray(value) || (formData && formData.options)
						? "array"
						: typeof value,
				row: 1,
				order: 1,
				value
			};

			Object.assign(uiSchema[key], formData);
		}
	});

	return uiSchema;
};

const getInitialValues = (config) => {
	const values = {};
	Object.entries(config).forEach(([key, value]) => {
		if (value instanceof Object && !Array.isArray(value)) {
			Object.assign(values, { [key]: getInitialValues(value) });
		} else {
			Object.assign(values, { [key]: value });
		}
	});

	return values;
};

const getNumberValidation = (min, max) => {
	let numberFieldRule = Yup.number()
		.typeError("Invalid")
		.required("Required")
		.integer("Invalid");

	if (min) {
		numberFieldRule = numberFieldRule.moreThan(min - 1, "Invalid");
	}
	if (max) {
		numberFieldRule = numberFieldRule.max(max, `Max: ${max}`);
	}

	return numberFieldRule;
};

const getValidationSchema = (config, formValues, customValidation) => {
	const validationSchema = {};
	//
	Object.entries(config).forEach(([key, value]) => {
		if (value instanceof Object && !Array.isArray(value)) {
			Object.assign(validationSchema, {
				[key]: Yup.object(getValidationSchema(value, formValues))
			});
		} else if (formValues[key]) {
			const { min, max } = formValues[key] || formValues[key] || {};
			if (min || max) {
				Object.assign(validationSchema, {
					[key]: getNumberValidation(min, max)
				});
			}
		}
	});
	//
	if (customValidation) {
		Object.assign(validationSchema, customValidation);
	}
	//
	return validationSchema;
};

const Configuration = forwardRef(
	(
		{
			configuration,
			customFormData,
			onSaveConfig,
			disabled,
			preview,
			secondaryActions,
			customValidation,
			// Sport-specific behavior plug-in (e.g. flag-football-configuration):
			// { sectionActions?, componentRenderers?, interceptChange?,
			//   isFullWidth? }. Omitted for sports with no custom form behavior.
			extension
		},
		ref
	) => {
		const commonClasses = commonStyles();
		const classes = configurationStyles({ preview });

		const getIconAdornment = (icon) => {
			return icon ? (
				<img className={classes.iconAdornment} src={IconMap[icon]} alt={icon} />
			) : null;
		};

		const toSortedRows = (props) => {
			const items = Object.entries(props)
				.filter(([, value]) => value.row && !value.disabled)
				.map(([, value]) => value);

			const rows = groupBy(items, "row");

			Object.entries(rows).forEach(([, v]) => {
				v.sort(sortBy("order"));
			});

			return rows;
		};

		const toSortedSections = (uiSchema) => {
			const sections = [];

			Object.entries(uiSchema).forEach(([key, value]) => {
				if (value.type === "object") {
					sections.push({
						key,
						title: value.title,
						row: value.row,
						order: value.order,
						rows: toSortedRows(value)
					});
				}
			});

			return sections.sort(sortBy("order"));
		};

		const uiSchema = useMemo(
			() => buildUiSchema(configuration, customFormData),
			[configuration, customFormData]
		);

		// Some fields (clock settings) stay editable while the form is
		// locked mid-game — Save/Cancel must stay usable for them too.
		const hasAlwaysEditableFields = useMemo(
			() =>
				Object.values(uiSchema).some(
					(section) =>
						section.type === "object" &&
						Object.values(section).some((field) => field && field.editableAfterStart)
				),
			[uiSchema]
		);
		const actionsDisabled = disabled && !hasAlwaysEditableFields;

		const validationSchema = useMemo(
			() =>
				Yup.object(
					getValidationSchema(configuration, customFormData, customValidation)
				),
			[configuration, customFormData, customValidation]
		);

		const initialValues = useMemo(
			() => getInitialValues(configuration),
			[configuration]
		);

		const formik = useFormik({
			enableReinitialize: true,
			initialValues,
			validationSchema,
			onSubmit: (values) => {
				onSaveConfig(values);
			}
		});
		const {
			handleSubmit,
			handleChange,
			resetForm,
			setFieldValue,
			setValues,
			errors,
			isValid
		} = formik;

		useImperativeHandle(ref, () => ({
			reset() {
				resetForm();
			}
		}));

		// Minimal form surface handed to the sport extension: read values, write
		// one field, or batch-write the whole values object (one render + one
		// validation pass — per-field writes are unbatched on React 16).
		const formApi = {
			values: formik.values,
			setFieldValue,
			setValues,
			disabled,
			preview
		};

		// The extension may claim a change (coupled-value bookkeeping, e.g.
		// flag football sanitizing markers on field-length edits); unclaimed
		// changes take the plain formik path.
		const setValue = (field, value) => {
			if (!extension?.interceptChange?.(field, value, formApi)) {
				setFieldValue(field, value);
			}
		};

		const createElement = (element) => {
			const {
				key,
				title,
				min,
				max,
				type,
				options,
				multiselect,
				disabled: elementDisabled,
				editableAfterStart,
				icon,
				component
			} = element;
			const inputProps =
				min || max
					? {
							min,
							max
					  }
					: {};
			// Fields flagged editableAfterStart (clock settings) ignore the
			// form-wide lock; the core save guard only lets their changes
			// through after game start.
			const fieldDisabled =
				(disabled && !editableAfterStart) || elementDisabled || preview;

			let returnValue = (
				<Typography variant="caption" key={key}>
					Unknown prop type {element.key}
				</Typography>
			);

			const customRenderer = extension?.componentRenderers?.[component];
			if (customRenderer) {
				returnValue = customRenderer(element, formApi);
			} else if (type === "boolean") {
				// Booleans without options render as a switch (booleans with
				// options come through as type "array" and render as a select).
				// The fieldset/legend mimic an outlined input so the control
				// lines up with the sibling fields, label in the notch.
				returnValue = (
					<fieldset key={key} className={classes.switchField}>
						<legend className={classes.switchFieldLabel}>{title}</legend>
						<Switch
							id={key}
							className={classes.switchFieldControl}
							checked={Boolean(getByPath(formik.values, key))}
							onChange={(e) => setValue(key, e.target.checked)}
							color="primary"
							disabled={fieldDisabled}
						/>
					</fieldset>
				);
			} else if (type === "number" || type === "string") {
				returnValue = (
					<TextField
						id={key}
						name={key}
						label={title}
						key={key}
						fullWidth
						type={type}
						value={getByPath(formik.values, key)}
						onChange={
							extension?.interceptChange
								? (e) => {
										// The extension may claim the change (e.g. field
										// length sanitizing); unclaimed changes take
										// formik's plain path.
										if (!extension.interceptChange(key, e.target.value, formApi)) {
											handleChange(e);
										}
								  }
								: handleChange
						}
						disabled={fieldDisabled}
						InputProps={{
							inputProps,
							endAdornment: getIconAdornment(icon)
						}}
						InputLabelProps={{ required: false }}
						helperText={getByPath(errors, key)}
						error={Boolean(getByPath(errors, key))}
						required
					/>
				);
			} else if (element.type === "array") {
				if (options) {
					if (multiselect) {
						returnValue = (
							<Autocomplete
								disabled={fieldDisabled}
								popupIcon={getIconAdornment(icon)}
								disableCloseOnSelect
								multiple
								limitTags={0}
								id={key}
								options={options}
								getOptionLabel={(option) => option.name}
								value={getByPath(formik.values, key).map((code) =>
									options.find((o) => o.code === code)
								)}
								onChange={(_, selections) => {
									setValue(
										key,
										selections.map(({ code }) => code)
									);
								}}
								renderOption={(option) => {
									return (
										<>
											<Checkbox
												checkedIcon={<CheckBoxIcon />}
												style={{ marginRight: 8 }}
												checked={getByPath(formik.values, key).some(
													(x) => x === option.code
												)}
											/>
											{option.name}
										</>
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
										variant="outlined"
										label={title}
									/>
								)}
							/>
						);
					} else {
						returnValue = (
							<Autocomplete
								id={key}
								options={options}
								value={options.find((o) => o.code === getByPath(formik.values, key))}
								getOptionLabel={(option) => option.name}
								getOptionSelected={(option, value) => {
									return option.code === value.code;
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										className={classes.autoComplete}
										label={title}
										variant="outlined"
										InputProps={{
											...params.InputProps,
											endAdornment: getIconAdornment(icon)
										}}
									/>
								)}
								onChange={(e, option) => setValue(key, option.code)}
								disabled={fieldDisabled}
								disableClearable
							/>
						);
					}
				} else {
					returnValue = (
						<TextField
							id={key}
							name={key}
							label={title}
							key={key}
							fullWidth
							type={type}
							value={getByPath(formik.values, key)}
							onChange={handleChange}
							disabled
							InputProps={{
								inputProps,
								endAdornment: getIconAdornment(icon)
							}}
							helperText={getByPath(errors, key)}
							error={Boolean(getByPath(errors, key))}
						/>
					);
				}
			}

			return returnValue;
		};

		const createRow = (row) =>
			row.map((r) => {
				const fullWidth = Boolean(extension?.isFullWidth?.(r, formApi));
				const defaultMd = preview ? 6 : 3;
				return (
					<Grid
						item
						xs={12}
						sm={fullWidth ? 12 : 6}
						md={fullWidth ? 12 : defaultMd}
						key={r.key}
					>
						{createElement(r)}
					</Grid>
				);
			});

		const createSection = (section) => {
			const sectionActions = extension?.sectionActions?.(section.key, formApi);
			return (
				<Box className={classes.section} key={section.title}>
					<Divider className={classes.sectionDivider} />
					<Box className={classes.sectionHeader}>
						<Typography variant="h5" className={classes.sectionLabel}>
							{section.title}
						</Typography>
						{sectionActions && (
							<Box className={classes.sectionActions}>{sectionActions}</Box>
						)}
					</Box>
					<Typography variant="body1">{section.description}</Typography>
					<Grid
						container
						spacing={4}
						direction="row"
						justifyContent="flex-start"
						alignItems="flex-start"
						className={classes.sectionRow}
					>
						{Object.values(section.rows).map(createRow)}
					</Grid>
				</Box>
			);
		};

		const buildUi = (schema) => {
			const sections = toSortedSections(schema);
			return <div>{sections.map(createSection)}</div>;
		};

		return uiSchema ? (
			<form className={commonClasses.form} onSubmit={handleSubmit}>
				{buildUi(uiSchema)}
				{!preview && (
					<Toolbar className={commonClasses.actionToolbar}>
						<Box className={classes.actionContainer}>
							{secondaryActions}
							<Box className={classes.primaryActionContainer}>
								<SecondaryButton
									color="secondary"
									label="Cancel"
									onClick={resetForm}
									disabled={actionsDisabled}
								/>
								<PrimaryButton
									type="submit"
									color="primary"
									label="Save"
									disabled={actionsDisabled || !isValid}
								/>
							</Box>
						</Box>
					</Toolbar>
				)}
			</form>
		) : null;
	}
);

export default Configuration;
